import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { AuthResponse } from '../types/api';
import mfaService from '../services/mfa.service';
import webAuthnService from '../services/webauthn.service';

interface AuthUser {
  sessionId: string;
  userId: number;
  username: string;
  role: string;
  createdAt: number;
  lastActivity: number;
  selectedDeviceId?: number;
  recordingEnabled: boolean;
  displayName?: string;
  email?: string;
}

interface MFAChallenge {
  userId: number;
  username: string;
  role: string;
  sessionId: string;
  timestamp: number;
  expiresAt: number;
  methods?: {
    totp: boolean;
    webauthn: boolean;
  };
}

interface WebAuthnSetup {
  required: boolean;
  deadline: string | null;
  canSkip: boolean;
  isUrgent?: boolean;
}

interface DeviceTrust {
  trusted: boolean;
  shouldPrompt: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  mfaRequired: boolean;
  mfaChallenge: MFAChallenge | null;
  webauthnSetup: WebAuthnSetup | null;
  deviceTrust: DeviceTrust | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: AuthUser }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'MFA_REQUIRED'; payload: { mfaChallenge: MFAChallenge } }
  | { type: 'WEBAUTHN_SETUP_REQUIRED'; payload: { webauthnSetup: WebAuthnSetup } }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_MFA' };

const initialState: AuthState = {
  user: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
  mfaRequired: false,
  mfaChallenge: null,
  webauthnSetup: null,
  deviceTrust: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        user: action.payload,
        isAuthenticated: true,
        error: null,
        mfaRequired: false,
        mfaChallenge: null,
        webauthnSetup: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        user: null,
        isAuthenticated: false,
        error: action.payload,
        mfaRequired: false,
        mfaChallenge: null,
        webauthnSetup: null,
      };
    case 'MFA_REQUIRED':
      return {
        ...state,
        isLoading: false,
        mfaRequired: true,
        mfaChallenge: action.payload.mfaChallenge,
        error: null,
      };
    case 'WEBAUTHN_SETUP_REQUIRED':
      console.log('WEBAUTHN_SETUP_REQUIRED reducer called with:', action.payload.webauthnSetup);
      return {
        ...state,
        isLoading: false,
        webauthnSetup: action.payload.webauthnSetup,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        error: null,
        mfaRequired: false,
        mfaChallenge: null,
        webauthnSetup: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'CLEAR_MFA':
      return {
        ...state,
        mfaRequired: false,
        mfaChallenge: null,
        webauthnSetup: null,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  loginWithWebAuthn: (username: string) => Promise<void>;
  loginWithPasskey: () => Promise<{ success: boolean; user?: any; message?: string }>;
  loginWithConditionalUI: (user: any) => void;
  logout: () => Promise<void>;
  checkAuth: (silent?: boolean) => Promise<void>;
  clearError: () => void;
  verifyMFA: (type: 'totp' | 'backup_code', code: string) => Promise<void>;
  skipWebAuthnSetup: () => Promise<void>;
  clearMFA: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = async (username: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const response: AuthResponse = await apiService.login({ username, password });
      
      if (response.mfaRequired && response.mfaChallenge) {
        // Include methods from the response if available
        const mfaChallengeWithMethods = {
          ...response.mfaChallenge,
          methods: response.methods || response.mfaChallenge.methods
        };
        dispatch({ type: 'MFA_REQUIRED', payload: { mfaChallenge: mfaChallengeWithMethods } });
      } else if (response.success && response.user) {
        console.log('Login successful, checking WebAuthn setup:', response.webauthnSetup);
        dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
        
        // Check if WebAuthn setup is also required after successful login
        if (response.webauthnSetup) {
          console.log('Dispatching WEBAUTHN_SETUP_REQUIRED with:', response.webauthnSetup);
          dispatch({ type: 'WEBAUTHN_SETUP_REQUIRED', payload: { webauthnSetup: response.webauthnSetup } });
        }
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.error || 'Login failed' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
    }
  };

  const loginWithPasskey = async (): Promise<{ success: boolean; user?: any; message?: string }> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const result = await webAuthnService.authenticate();
      
      if (result.success && result.user) {
        // Create auth user object from WebAuthn result
        const authUser: AuthUser = {
          sessionId: result.user.sessionId || `webauthn_${Date.now()}`,
          userId: result.user.id,
          username: result.user.username,
          role: result.user.role,
          createdAt: Date.now(),
          lastActivity: Date.now(),
          recordingEnabled: true
        };
        
        dispatch({ type: 'AUTH_SUCCESS', payload: authUser });
        return { success: true, user: authUser };
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: result.message || 'Passkey authentication failed' });
        return { success: false, message: result.message || 'Passkey authentication failed' };
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Passkey authentication failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      return { success: false, message: errorMessage };
    }
  };

  const loginWithWebAuthn = async (username: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {
      const result = await webAuthnService.authenticate(username);
      
      if (result.success && result.user) {
        // Create auth user object from WebAuthn result
        const authUser: AuthUser = {
          sessionId: result.user.sessionId || `webauthn_${Date.now()}`,
          userId: result.user.id,
          username: result.user.username,
          role: result.user.role,
          createdAt: Date.now(),
          lastActivity: Date.now(),
          recordingEnabled: true
        };
        
        dispatch({ type: 'AUTH_SUCCESS', payload: authUser });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: result.message || 'WebAuthn authentication failed' });
      }
    } catch (error: any) {
      const errorMessage = error.message || 'WebAuthn authentication failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
    }
  };

  const loginWithConditionalUI = (user: any): void => {
    try {
      // Create auth user object from conditional UI result
      const authUser: AuthUser = {
        sessionId: user.sessionId || `webauthn_conditional_${Date.now()}`,
        userId: user.id,
        username: user.username,
        role: user.role,
        createdAt: Date.now(),
        lastActivity: Date.now(),
        recordingEnabled: true
      };
      
      dispatch({ type: 'AUTH_SUCCESS', payload: authUser });
    } catch (error: any) {
      const errorMessage = error.message || 'Conditional UI authentication failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const checkAuth = async (silent: boolean = false): Promise<void> => {
    if (!silent) {
      dispatch({ type: 'AUTH_START' });
    }
    try {
      const response: AuthResponse = await apiService.checkAuth();
      console.log('checkAuth response:', response);
      if (response.success && response.user) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
        
        // Check if WebAuthn setup is required after successful auth check
        if (response.webauthnSetup) {
          console.log('checkAuth: Dispatching WEBAUTHN_SETUP_REQUIRED with:', response.webauthnSetup);
          dispatch({ type: 'WEBAUTHN_SETUP_REQUIRED', payload: { webauthnSetup: response.webauthnSetup } });
        }
      } else {
        if (!silent) {
          dispatch({ type: 'AUTH_FAILURE', payload: 'Not authenticated' });
        } else {
          // Silent check - just clear loading state without error
          dispatch({ type: 'LOGOUT' });
        }
      }
    } catch (error: any) {
      console.log('checkAuth error:', error);
      if (!silent) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication check failed' });
      } else {
        // Silent check - just clear loading state without error
        dispatch({ type: 'LOGOUT' });
      }
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const verifyMFA = async (type: 'totp' | 'backup_code', code: string): Promise<void> => {
    if (!state.mfaChallenge) {
      throw new Error('No MFA challenge available');
    }

    dispatch({ type: 'AUTH_START' });
    try {
      const response = await mfaService.verifyMFA({
        type,
        code,
        userId: state.mfaChallenge.userId
      });

      if (response.success && response.user) {
        dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: response.message || 'MFA verification failed' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'MFA verification failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
    }
  };

  const skipWebAuthnSetup = async (): Promise<void> => {
    try {
      await mfaService.skipWebAuthnSetup();
      dispatch({ type: 'CLEAR_MFA' });
    } catch (error: any) {
      console.error('Skip WebAuthn setup error:', error);
      // Don't log user out, just clear MFA state so they can continue
      dispatch({ type: 'CLEAR_MFA' });
    }
  };

  const clearMFA = (): void => {
    dispatch({ type: 'CLEAR_MFA' });
  };

  // Check authentication on mount (silently)
  useEffect(() => {
    checkAuth(true);
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    loginWithWebAuthn,
    loginWithPasskey,
    loginWithConditionalUI,
    logout,
    checkAuth,
    clearError,
    verifyMFA,
    skipWebAuthnSetup,
    clearMFA,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;